<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            Les photos 360° floues sont presque toujours causées par l'une des
            six problèmes techniques spécifiques, chacun avec une solution
            claire. Comprendre quelle cause affecte vos images vous permet de
            corriger le problème efficacement — souvent sans avoir à refaire la
            session de capture.
          </p>
        </section>

        <section>
          <h2>Cause 1 : Flou de bougé (camera shake)</h2>
          <h3>Comment l'identifier</h3>
          <p>
            Le flou de bougé se manifeste par un étirement directionnel de
            l'image — les objets semblent traîner dans une direction. Il
            apparaît surtout dans les zones peu éclairées où l'appareil photo
            allonge automatiquement le temps d'obturation pour compenser.
          </p>
          <h3>Solution : trépied et vitesse d'obturation</h3>
          <p>
            Solution principale : utilisez un trépied. La capture 360° à main
            levée dans des conditions insuffisamment éclairées produit du flou
            de bougé. Si vous n'avez pas de trépied, verrouillez la vitesse
            d'obturation à 1/60 s minimum en mode Pro.
          </p>
        </section>

        <section>
          <h2>Cause 2 : Flou dû aux sujets en mouvement</h2>
          <p>
            Des personnes, animaux ou objets qui bougent pendant la capture
            créent des fantômes et des incohérences dans l'image assemblée.
          </p>
          <h3>Solution : attendre et recapturer</h3>
          <p>
            Attendez que les personnes sortent du champ avant de capturer les
            panoramas critiques. Pour les espaces publics, capturez tôt le matin
            ou après les heures d'ouverture quand le trafic est minimal.
          </p>
        </section>

        <section>
          <h2>Cause 3 : Erreurs d'assemblage (coutures visibles)</h2>
          <p>
            Des coutures visibles dans l'image assemblée indiquent un
            chevauchement insuffisant entre les images capturées ou un mouvement
            trop rapide pendant la capture.
          </p>
          <h3>Solution : couvrir les zones manquantes</h3>
          <p>
            Utilisez la carte de progression de Travvir pour vérifier que toutes
            les zones sont correctement couvertes avant de terminer la capture.
            Recapturez les zones spécifiques indiquées comme incomplètes.
          </p>
        </section>

        <section>
          <h2>Cause 4 : Mauvaise exposition</h2>
          <p>
            Les images surexposées (trop lumineuses) ou sous-exposées (trop
            sombres) perdent les détails dans les hautes lumières ou les ombres,
            ce qui se traduit par des zones sans textures.
          </p>
          <h3>Solution : verrouillage de l'exposition</h3>
          <p>
            Verrouillez l'exposition avant la capture. Sur iPhone, appuyez
            longuement jusqu'à voir "AE/AF verrouillé". Sur Android, utilisez le
            mode Pro pour fixer l'ISO et l'obturation. L'IA de Travvir applique
            également une correction automatique à l'assemblage.
          </p>
        </section>

        <section>
          <h2>Cause 5 : Netteté insuffisante (mise au point floue)</h2>
          <p>
            Si l'image entière manque de netteté sans flou directionnel, la mise
            au point est probablement verrouillée sur une zone incorrecte.
          </p>
          <h3>Solution : mise au point sur infini</h3>
          <p>
            Pour les espaces intérieurs de taille normale, réglez manuellement
            la mise au point sur infini (ou sur un objet à 2–3 mètres de
            distance). Évitez que l'autofocus se fixe sur des détails très
            proches.
          </p>
        </section>

        <section>
          <h2>Cause 6 : Mauvaise résolution de sortie</h2>
          <p>
            Si votre photo 360° paraît globalement douce et sans détail,
            vérifiez les paramètres de résolution de sortie dans les réglages de
            l'application.
          </p>
          <h3>Solution : maximiser la résolution</h3>
          <p>
            Travvir exporte automatiquement à la résolution maximale supportée
            par votre appareil. Assurez-vous de ne pas avoir activé de mode
            compression ou qualité réduite pour économiser l'espace de stockage.
          </p>
        </section>

        <section>
          <h2>Questions fréquentes</h2>

          <h3>
            Pourquoi mes photos 360° sont-elles floues uniquement en bas ?
          </h3>
          <p>
            Le flou concentré en bas de l'image indique généralement un problème
            de couverture du sol — une zone souvent difficile à capturer lors
            d'une rotation standard. Inclinez le téléphone vers le bas lors des
            dernières étapes de la capture pour compléter cette zone.
          </p>

          <h3>L'IA de Travvir peut-elle corriger les photos déjà floues ?</h3>
          <p>
            L'IA de Travvir applique une réduction du bruit et une netteté
            automatique au moment du traitement. Pour les flous légers, cette
            correction est souvent suffisante. Les flous sévères nécessitent une
            nouvelle capture.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Continuez la Lecture</h2>
          <ul>
          <li><a href="/blog/fr/taille-images-visite-virtuelle-360">Taille et Résolution des Images pour Visites Virtuelles 360° 2026</a></li>
          <li><a href="/blog/fr/erreurs-photographie-360">10 Erreurs de Photographie 360° à Éviter en 2026</a></li>
          <li><a href="/blog/fr/photos-360-avec-smartphone">Comment Prendre des Photos 360° avec un Smartphone : Guide Complet</a></li>
          <li><a href="/blog/fr/photographie-interieure-360-visite-virtuelle">Photographie d'Intérieur pour Visites Virtuelles 360° : Guide Technique</a></li>
          <li><a href="/blog/fr/meilleure-app-camera-360-android">Meilleure Application Caméra 360° Android 2026 : Comparatif Complet</a></li>
          <li><a href="/blog/fr/meilleure-app-camera-360-iphone">Meilleure Application Caméra 360° iPhone 2026 : Comparatif Complet</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "corriger-photos-360-floues",
);

useBlogSeo({
  title: "Photos 360° Floues : 6 Causes et Solutions Concrètes 2026 | Travvir",
  description:
    "Diagnostiquez et corrigez les photos 360° floues : flou de bougé, erreurs d'assemblage, mauvaise exposition, mise au point incorrecte. Solutions pratiques pour chaque cause.",
  keywords:
    "photo 360 floue correction, flou photo 360, erreur assemblage 360, photo 360 qualité, améliorer photo 360 smartphone",
  slug: "corriger-photos-360-floues",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question:
        "Pourquoi mes photos 360° sont-elles floues uniquement en bas ?",
      answer:
        "Le flou concentré en bas indique un problème de couverture du sol. Inclinez le téléphone vers le bas lors des dernières étapes de capture.",
    },
    {
      question: "L'IA de Travvir peut-elle corriger les photos déjà floues ?",
      answer:
        "Oui pour les flous légers grâce à la réduction du bruit et la netteté automatique. Les flous sévères nécessitent une nouvelle capture.",
    },
  ],
});

</script>
